/**
 * StatsPage.jsx — standalone /stats route showing the user's
 * SmarterHubStats panel. Admins automatically get the 3-mode toggle;
 * everyone else sees their own role's panel locked.
 *
 * Feb 13 2026 — User feedback ("Replace all these old way of showing
 * stats with the new improved one. For all users") — promoted the
 * StreakHeroCard onto this page above the SmarterHubStats panel so
 * /stats now matches the layout already shipped on /profile/stats.
 * Role resolution mirrors ProfileStats: writer space (or admin viewing
 * as writer) renders the writer streak card, everyone else gets the
 * reader streak card.
 */
import React, { useEffect, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import SmarterHubStats from '../components/SmarterHubStats';
import StreakHeroCard from '../components/StreakHeroCard';
import { checkAuth } from '../utils/authUtils';
import {
  effectiveRoleFor,
  getActiveSpace,
  onSpaceChange,
} from '../utils/spaceContext';

const StatsPage = () => {
  const [user, setUser] = useState(null);
  // Re-render whenever the writer↔reader (or admin viewing-as) axis
  // changes so the streak card swaps in real time without a navigation.
  const [, setSpaceTick] = useState(0);

  useEffect(() => onSpaceChange(() => setSpaceTick((n) => n + 1)), []);

  useEffect(() => {
    let alive = true;
    checkAuth()
      .then((u) => { if (alive) setUser(u || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const isAdmin = user?.role === 'admin';
  // Streak role: admins/writers viewing the writer space see the writer
  // streak, everyone else (incl. readers, coadmins, kings) sees reader.
  const effective = user ? effectiveRoleFor(user.role, getActiveSpace()) : 'user';
  const streakRole = effective === 'writer' ? 'writer' : 'reader';

  return (
    <main
      className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 sm:py-10 pb-32"
      data-testid="stats-page"
    >
      <header className="mb-6 flex items-center gap-3">
        <BarChart3 className="w-6 h-6 text-cyan-300" />
        <h1
          className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight leading-none"
          data-testid="stats-page-title"
        >
          My Stats<span className="text-cyan-300">.</span>
        </h1>
      </header>

      {/* New streak hero card — sits above the legacy SmarterHubStats
          gradient panel so the most engaging signal (streak + savers)
          is the first thing users see on /stats. */}
      <div className="mb-6">
        <StreakHeroCard role={streakRole} />
      </div>

      <SmarterHubStats isAdmin={isAdmin} allowToggle />
    </main>
  );
};

export default StatsPage;
